import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IRecipient } from '@tiendanube/common';
import { useNavegate } from 'App/hooks';
import { useAppDispatch } from 'App/store';
import { dateFormat, Format } from 'commons/utils/date';
import { PAYMENTS_ROUTES } from 'domains/Payments/paymentsRoutes';
import {
  getRecipientAction,
  getRecipientData,
  getRecipientStatus,
  resetGetRecipientAction,
} from '../recipientSlice';

const extractInstallData = (recipientData: IRecipient) => {
  const { accounts, kyc, fiscalType } = recipientData;
  return {
    isKycCompleted: kyc?.status === 'COMPLETED',
    isIndividual: fiscalType === 'INDIVIDUAL',
    isAccountActive: !!accounts && accounts[0]?.status === 'ACTIVE',
    fiscalType,
    kyc,
  };
};

const useGetRecipient = (
  openCompanyModal?: (stripeFormLink?: string) => Promise<void> | void,
  setShowIndividualForm?: Dispatch<SetStateAction<boolean>>,
) => {
  const dispatch = useAppDispatch();
  const { goTo } = useNavegate();

  const recipientData = useSelector(getRecipientData);
  const recipientStatus = useSelector(getRecipientStatus);

  const fetchRecipient = useCallback(
    (storeId: string, withRetry?: boolean) => {
      dispatch(getRecipientAction({ storeId, withRetry }));
    },
    [dispatch],
  );

  const resetRecipientData = useCallback(() => {
    dispatch(resetGetRecipientAction());
  }, [dispatch]);

  const handleCompanyOnboarding = useCallback(
    (kyc: IRecipient['kyc']) => {
      if (openCompanyModal) {
        openCompanyModal(kyc?.gatewayOnboarding?.url);
      }
    },
    [openCompanyModal],
  );

  const handleInactiveAccount = useCallback(
    (kyc: IRecipient['kyc'], isIndividual: boolean) => {
      if (isIndividual) {
        const requestDate = kyc?.tosAcceptanceDate
          ? dateFormat(kyc.tosAcceptanceDate, Format.DD_MM_YY)
          : '';
        return goTo(
          `/nuvempago/#/landing?status=delayedValidation&requestDate=${requestDate}`,
        );
      }

      handleCompanyOnboarding(kyc);
    },
    [goTo, handleCompanyOnboarding],
  );

  const handleIndividualOnboarding = useCallback(() => {
    if (setShowIndividualForm) {
      setShowIndividualForm(true);
    }
  }, [setShowIndividualForm]);

  const navigateOnboarding = useCallback(
    async (recipientData: IRecipient) => {
      const { isKycCompleted, isAccountActive, isIndividual, fiscalType, kyc } =
        extractInstallData(recipientData);
      if (isKycCompleted && isAccountActive) {
        return goTo(PAYMENTS_ROUTES.PAYMENTS_BASE_ROUTE);
      }

      if (isKycCompleted && !isAccountActive) {
        return handleInactiveAccount(kyc, isIndividual);
      }

      if (fiscalType === 'COMPANY') {
        return handleCompanyOnboarding(kyc);
      }

      if (isIndividual) {
        return handleIndividualOnboarding();
      }
    },
    [
      goTo,
      handleCompanyOnboarding,
      handleInactiveAccount,
      handleIndividualOnboarding,
    ],
  );

  useEffect(() => {
    if (!recipientStatus.isSuccess) return;

    if (recipientData?.id) {
      navigateOnboarding(recipientData);
    } else {
      goTo(PAYMENTS_ROUTES.nuvempagoInitSettings);
    }
  }, [recipientData, recipientStatus.isSuccess, navigateOnboarding, goTo]);

  return {
    fetchRecipient,
    recipientData,
    ...recipientStatus,
    resetRecipientData,
  };
};

export default useGetRecipient;
